import React, { useState, useEffect, useMemo } from 'react';
import DropdownSelect from '../../../components/DropdownSelect';
import './PlanCard.css';

export function PlanCard({ plan, onSelect, userMembership, onManageSubscription }) {
  const isFreePlan = (plan?.uniqueId || '').toLowerCase() === 'free';

  // Pro/Max: per-card billing (monthly | annual). Free: always one-time.
  const getInitialBillingCycle = () => {
    if (isFreePlan) return 'one-time';
    const planMatches = userMembership?.planId &&
      userMembership.planId.toLowerCase() === (plan?.uniqueId || '').toLowerCase();
    if (planMatches && userMembership?.billingCycle) {
      const bc = userMembership.billingCycle.toLowerCase();
      if (bc === 'monthly' || bc === 'annual') return bc;
    }
    return 'monthly';
  };
  const [billingCycle, setBillingCycle] = useState(getInitialBillingCycle);
  const effectiveBillingCycle = isFreePlan ? 'one-time' : billingCycle;

  // Get tiers from plan
  const tiers = useMemo(() => plan.tiers || [], [plan.tiers]);
  const hasMultipleTiers = tiers.length > 1;

  // Initialize with first tier
  const [selectedTierId, setSelectedTierId] = useState(
    tiers.length > 0 ? tiers[0].id : null
  );

  // Find selected tier
  const selectedTier = tiers.find(t => t.id === selectedTierId) || tiers[0];

  // Check if this plan is the user's current plan
  // Must match: planId, tierId (if both exist), AND billingCycle
  const planMatches = userMembership?.planId && 
    userMembership.planId.toLowerCase() === plan.uniqueId?.toLowerCase();
  
  const tierMatches = !userMembership?.tierId || !selectedTier?.id || 
    userMembership.tierId.toLowerCase() === selectedTier.id.toLowerCase();
  
  // Free plan is displayed as "one-time" while the pricing toggle is monthly/annual.
  // For free users, we still want the Free card to show "Manage Plan" regardless of the toggle state.
  const billingCycleMatches = isFreePlan
    ? true
    : (userMembership?.billingCycle &&
        userMembership.billingCycle.toLowerCase() === effectiveBillingCycle?.toLowerCase());
  
  const isCurrentPlan = userMembership && planMatches && tierMatches && billingCycleMatches;

  // If this card matches the user's current plan, default the tier dropdown to their tier
  // so "Manage Plan" is visible immediately.
  useEffect(() => {
    if (!planMatches) return;
    const membershipTierId = userMembership?.tierId;
    if (!membershipTierId) return;
    if (!tiers || tiers.length === 0) return;

    const targetTier = tiers.find(
      (t) => (t?.id || '').toLowerCase() === String(membershipTierId).toLowerCase()
    );
    if (!targetTier?.id) return;

    setSelectedTierId((prev) => (prev !== targetTier.id ? targetTier.id : prev));
  }, [planMatches, tiers, userMembership?.tierId]);

  // Reset to first tier if tiers change
  useEffect(() => {
    if (tiers.length > 0 && !tiers.find(t => t.id === selectedTierId)) {
      setSelectedTierId(tiers[0].id);
    }
  }, [tiers, selectedTierId]);

  // Calculate price based on billing cycle
  const calculatePrice = (monthlyPrice, billingCycle, discount = 0) => {
    const basePrice = parseFloat(monthlyPrice) || 0;
    const discountPercent = parseFloat(discount) || 0;

    if (billingCycle === 'monthly') {
      return basePrice;
    } else if (billingCycle === 'annual') {
      return basePrice * 12 * (1 - discountPercent / 100);
    } else if (billingCycle === 'one-time') {
      // One-time represents 3 years without discount
      return basePrice * 12 * 3;
    }
    return basePrice;
  };

  const monthlyPrice = parseFloat(selectedTier?.price || 0);
  const discount = parseFloat(plan.discount || 0);
  const calculatedPrice = calculatePrice(monthlyPrice, effectiveBillingCycle, discount);

  // Format description into features list with source tracking
  const planFeatures = plan.description
    ? plan.description.split('\n').filter(line => line.trim().startsWith('-')).map(feature => ({
        text: feature,
        source: 'plan'
      }))
    : [];
  
  // Format tier description into features list with source tracking
  const tierFeatures = selectedTier?.description
    ? selectedTier.description.split('\n').filter(line => line.trim().startsWith('-')).map(feature => ({
        text: feature,
        source: 'tier'
      }))
    : [];
  
  // Combine plan and tier features
  const features = [...planFeatures, ...tierFeatures];

  // Create dropdown options from tiers
  const tierOptions = tiers.map(tier => ({
    value: tier.id,
    label: tier.displayName || tier.id,
  }));

  const billingOptions = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'annual', label: 'Annual' },
  ];

  const handleTierChange = (tierId) => {
    setSelectedTierId(tierId);
  };

  const handleSelect = (e) => {
    // Prevent card click from triggering when clicking dropdown or button
    if (e.target.closest('.dropdown-select') || e.target.closest('button')) {
      return;
    }

    if (isCurrentPlan && onManageSubscription) {
      onManageSubscription();
    } else if (onSelect && selectedTier) {
      onSelect({
        planId: plan.uniqueId,
        tierId: selectedTier.id,
        billingCycle: effectiveBillingCycle,
        price: calculatedPrice,
        monthlyPrice,
        discount,
      });
    }
  };

  return (
    <div
      className="plan-card"
      style={{
        width: '320px',
        height: '550px',
        flexShrink: 0,
        boxSizing: 'border-box',
        background: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        padding: '24px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}
      onClick={handleSelect}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(139, 0, 0, 0.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Top Section: Title, Price, Credits */}
      <div style={{ flexShrink: 0 }}>
        {/* Plan Header */}
        <div style={{ marginBottom: '16px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap',
              marginBottom: '8px',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: '600',
                color: '#fff',
              }}
            >
              {plan.displayName}
            </h3>
            {selectedTier && selectedTier.displayName !== 'Basic' && (
              <div
                style={{
                  display: 'inline-block',
                  padding: '4px 8px',
                  background: 'rgba(139, 0, 0, 0.3)',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#ff6b6b',
                  fontWeight: '500',
                }}
              >
                {selectedTier.displayName}
              </div>
            )}
          </div>
        </div>

        {/* Price and Credits Row */}
        <div style={{ marginBottom: '16px' }}>
          {/* Price Display */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '4px' }}>
              <span
                style={{
                  fontSize: '36px',
                  fontWeight: '700',
                  color: '#fff',
                  lineHeight: '1',
                }}
              >
                ${calculatedPrice.toFixed(2)}
              </span>
              <span
                style={{
                  fontSize: '16px',
                  color: '#999',
                  fontWeight: '400',
                }}
              >
                {isFreePlan
                  ? ''
                  : effectiveBillingCycle === 'annual'
                    ? '/year'
                    : effectiveBillingCycle === 'monthly'
                      ? '/month'
                      : ' one-time'}
              </span>
            </div>
            {!isFreePlan && effectiveBillingCycle === 'annual' && discount > 0 && (
              <div
                style={{
                  fontSize: '11px',
                  color: '#4ade80',
                  fontWeight: '500',
                }}
              >
                Save ${(monthlyPrice * 12 - calculatedPrice).toFixed(2)}/year ({discount}% off)
              </div>
            )}
          </div>

          {/* Credits Display */}
          {selectedTier && selectedTier.credits > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                background: 'rgba(139, 0, 0, 0.12)',
                borderRadius: '6px',
                border: '1px solid rgba(139, 0, 0, 0.2)',
              }}
            >
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#ff6b6b',
                  lineHeight: '1',
                }}
              >
                {selectedTier.credits.toLocaleString()}
              </div>
              <div
                style={{
                  fontSize: '11px',
                  color: '#aaa',
                  fontWeight: '400',
                }}
              >
                credits/month
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Middle Section: Scrollable Description/Features */}
      <div
        className="plan-card-description-scroll"
        style={{
          flex: '1 1 auto',
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          marginBottom: '20px',
        }}
      >
        {features.length > 0 && (
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
            }}
          >
            {features.map((feature, index) => (
              <li
                key={index}
                style={{
                  padding: '4px 0',
                  fontSize: '14px',
                  color: '#ccc',
                  display: 'flex',
                  alignItems: 'start',
                  gap: '8px',
                }}
              >
                <span style={{ 
                  color: feature.source === 'tier' ? '#ff6b6b' : '#0f0', 
                  fontSize: '16px',
                  fontWeight: feature.source === 'tier' ? 'bold' : 'normal'
                }}>
                  {feature.source === 'tier' ? '+' : '✓'}
                </span>
                <span>{feature.text.replace(/^-\s*/, '')}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Bottom Section: Dropdowns and Button */}
      <div style={{ flexShrink: 0 }}>
        {/* Tier and Billing dropdowns - same row, same width */}
        {(hasMultipleTiers || !isFreePlan) && (
          <div
            className="plan-card-dropdowns-row"
            style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '12px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {hasMultipleTiers && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <DropdownSelect
                  value={selectedTierId}
                  options={tierOptions}
                  onValueChange={handleTierChange}
                  placeholder="Select Tier"
                  size="sm"
                  className="plan-card-tier-select"
                  menuClassName="plan-card-tier-menu"
                />
              </div>
            )}
            {!isFreePlan && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <DropdownSelect
                  value={billingCycle}
                  options={billingOptions}
                  onValueChange={(val) => setBillingCycle(val)}
                  placeholder="Billing"
                  size="sm"
                  className="plan-card-tier-select"
                  menuClassName="plan-card-tier-menu"
                />
              </div>
            )}
          </div>
        )}

        {/* CTA Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            // If this is the current plan, open customer portal
            if (isCurrentPlan && onManageSubscription) {
              onManageSubscription();
            }
            // Otherwise, proceed with plan selection
            else if (onSelect && selectedTier) {
              onSelect({
                planId: plan.uniqueId,
                tierId: selectedTier.id,
                billingCycle: effectiveBillingCycle,
                price: calculatedPrice,
                monthlyPrice,
                discount,
              });
            }
          }}
          style={{
            width: '100%',
            padding: '12px 24px',
            background: isCurrentPlan
              ? 'rgba(100, 100, 100, 0.3)' 
              : 'rgba(139, 0, 0, 0.8)',
            border: isCurrentPlan
              ? '1px solid rgba(100, 100, 100, 0.5)'
              : '1px solid rgba(139, 0, 0, 1)',
            borderRadius: '8px',
            color: '#fff',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            if (!isCurrentPlan) {
              e.currentTarget.style.background = 'rgba(139, 0, 0, 1)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isCurrentPlan) {
              e.currentTarget.style.background = 'rgba(139, 0, 0, 0.8)';
            }
          }}
        >
          {isCurrentPlan 
            ? 'Manage Plan'
            : 'Select Plan'}
        </button>
      </div>
    </div>
  );
}
